import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import PeopleSelectionLists from "./components/PeopleSelectionLists";
import { Facility, Person, Event } from "./CreateGlobalInteractionSideSheet";
import { InteractionInput } from "@/components/interactions/types";
import { UnavailablePersonBanner } from "./components/UnavailablePersonBanner";
import { AlgoliaSearchResults, type PersonHit, getPersonDisplayName } from "@/components/AlgoliaSearch";
import { useEffect } from "react";
import { renderCreateInteractionPersonHit } from "./createInteractionFuzzyHitRenderer";
import { useCreateInteractionFuzzySearch } from "./useCreateInteractionFuzzySearch";

type ManualType = {
  gatheredEvents: Event[];
  gatheredFacilities: Facility[];
  ineligiblePerson: Person | undefined;
  peopleArray: Person[];
  setIneligiblePerson: (value: Person | undefined) => void;
  setPeoplePGAId: (value: string) => void;
  setSearchedForPerson: (value: boolean) => void;
  setVariables: (value: InteractionInput) => void;
  variables: InteractionInput;
};

export const MultiPersonAdderManual = ({
  ineligiblePerson,
  gatheredEvents,
  gatheredFacilities,
  peopleArray,
  setIneligiblePerson,
  setPeoplePGAId,
  setSearchedForPerson,
  setVariables,
  variables
}: ManualType) => {
  const {
    query: personQuery,
    displayValue,
    results: personResults,
    isLoading: personLoading,
    failed: personFailed,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handlePersonClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useCreateInteractionFuzzySearch({
    entity: "people",
    getDisplayValue: getPersonDisplayName,
    onSelect: (hit: PersonHit) => {
      const pgaId = hit.pga_id || hit.legacy_id;
      if (pgaId) {
        setPeoplePGAId(pgaId);
        setSearchedForPerson(true);
        setIneligiblePerson(undefined);
      }
    }
  });

  const hasActivePersonQuery = personQuery.trim() !== "";

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  useEffect(() => {
    if (displayValue) {
      setDisplayValue(undefined);
      focusInput();
    }
  }, [displayValue, setDisplayValue, focusInput]);

  const handleClearPerson = () => {
    handlePersonClear();
    setDisplayValue(undefined);
    setPeoplePGAId("");
    setSearchedForPerson(false);
    setIneligiblePerson(undefined);
  };

  const personTrailingIcon =
    displayValue || personQuery ? (
      <button
        type="button"
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearPerson}
        aria-label="Clear search query"
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const selectedPeople = peopleArray.filter((person) => person.selected);
  const availablePeople = peopleArray.filter(
    (person) =>
      !person.selected &&
      !gatheredFacilities.some((facility) => facility.id === person.primaryFacility?.id) &&
      !gatheredEvents.some((event) => event.id === person.eventId)
  );

  return (
    <div className="space-y-6">
      <div ref={searchBoxRef}>
        <TextField
          label="Person"
          placeholder="Search for a person"
          value={displayValue || personQuery}
          onChangeText={(value) => {
            setDisplayValue(undefined);
            handleQueryChange(value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          leadingIcon="search"
          trailingIcon={personTrailingIcon}
          variant="outlined"
          className="w-full"
          supportingText="Search for and individually add multiple people"
          data-testid="manual-person-search-input"
        />
      </div>

      {hasActivePersonQuery && (
        <AlgoliaSearchResults
          results={personResults}
          query={personQuery}
          isLoading={personLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderCreateInteractionPersonHit}
          noResultsMessage={personFailed ? "Couldn't load people" : "No results found"}
          className="mt-4"
        />
      )}

      {ineligiblePerson && <UnavailablePersonBanner person={ineligiblePerson} />}

      {!hasActivePersonQuery && (
        <PeopleSelectionLists
          selectedPeople={selectedPeople}
          availablePeople={availablePeople}
          onSelect={(person) => {
            person.selected = true;
            setVariables({ ...variables, participantIds: [...(variables.participantIds || []), person.id] });
          }}
          onDeselect={(person) => {
            person.selected = false;
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => id !== person.id)
            });
          }}
          onSelectAll={() => {
            availablePeople.forEach((p) => {
              p.selected = true;
            });
            setVariables({
              ...variables,
              participantIds: [...(variables.participantIds || []), ...availablePeople.map((p) => p.id)]
            });
          }}
          onDeselectAll={() => {
            selectedPeople.forEach((p) => {
              p.selected = false;
            });
            const idsToRemove = new Set(selectedPeople.map((p) => p.id));
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => !idsToRemove.has(id))
            });
          }}
        />
      )}
    </div>
  );
};
